import React, { useEffect, useState } from "react";

import { Box } from "@mui/material";

import "../../css/funnel.css";

const PreviewContainer = ({
    steps = [],
    templateFields = {},
    selectedStepId,
    onStepSelect,
}) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [userResponses, setUserResponses] = useState({});
    const [stepHistory, setStepHistory] = useState([]);
    const [processingState, setProcessingState] = useState({
        active: false,
        taskIndex: 0,
        progress: 0,
    });
    const [carouselState, setCarouselState] = useState({}); // stepId -> index
    const [isCompleted, setIsCompleted] = useState(false);

    // Reset when steps change
    useEffect(() => {
        setCurrentStepIndex(0);
        setUserResponses({});
        setStepHistory([]);
        setProcessingState({ active: false, taskIndex: 0, progress: 0 });
        setCarouselState({});
        setIsCompleted(false);
    }, [steps]);

    const currentStep = steps[currentStepIndex];
    const progress = isCompleted
        ? 100
        : steps.length > 0
          ? ((currentStepIndex + 1) / steps.length) * 100
          : 0;

    // Handle processing step auto-advance
    useEffect(() => {
        if (!currentStep) return;
        const stepType = normalizeType(currentStep.type);
        if (stepType !== "processing") {
            setProcessingState({ active: false, taskIndex: 0, progress: 0 });
            return;
        }

        const tasks = currentStep.attributes?.processing_steps || [];
        if (!tasks.length) {
            handleResponse(currentStep.id, "completed");
            return;
        }

        let cancelled = false;
        let idx = 0;
        const runTask = () => {
            if (cancelled || idx >= tasks.length) {
                handleResponse(currentStep.id, "completed");
                return;
            }
            const duration = Number(tasks[idx]?.duration || 1500);
            const start = Date.now();
            const tick = () => {
                if (cancelled) return;
                const pct = Math.min(
                    100,
                    Math.round(((Date.now() - start) / duration) * 100),
                );
                setProcessingState({
                    active: true,
                    taskIndex: idx,
                    progress: pct,
                });
                if (pct >= 100) {
                    idx += 1;
                    setTimeout(runTask, 200);
                } else {
                    requestAnimationFrame(tick);
                }
            };
            requestAnimationFrame(tick);
        };
        runTask();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep?.id]);

    const nextStep = () => {
        if (currentStepIndex < steps.length - 1) {
            setStepHistory((prev) => [...prev, currentStepIndex]);
            setCurrentStepIndex((prev) => prev + 1);
        } else {
            // Reached the last step - show completion screen
            setIsCompleted(true);
        }
    };

    const previousStep = () => {
        if (stepHistory.length > 0) {
            const prevIndex = stepHistory[stepHistory.length - 1];
            setStepHistory((prev) => prev.slice(0, -1));
            setCurrentStepIndex(prevIndex);
        }
    };

    const skipStep = () => {
        setUserResponses((prev) => ({
            ...prev,
            [currentStep.id]: "skipped",
        }));
        nextStep();
    };

    const handleResponse = (stepId, value) => {
        setUserResponses((prev) => ({
            ...prev,
            [stepId]: value,
        }));
    };

    const normalizeType = (type) => {
        switch (type) {
            case "radio-button":
                return "radio";
            case "star_rating":
                return "rating";
            case "nps_rating":
                return "nps";
            case "range_slider":
                return "range";
            case "categorical_slider":
                return "slider";
            case "image_carousel":
                return "carousel";
            case "grid_selector":
                return "grid_selector"; // handled explicitly
            case "grid_selector_multiple":
                return "grid_selector_multiple"; // handled explicitly
            case "file_upload":
                return "file";
            case "processing_step":
                return "processing";
            default:
                return type;
        }
    };

    const renderMedia = (step) => {
        const imageUrl = step.image_url;
        const image = step?.image;
        return (
            <>
                {imageUrl && (
                    <img
                        src={imageUrl}
                        className="step-image"
                        alt={step.title || "Image"}
                        style={{
                            maxHeight: "10rem",
                        }}
                    />
                )}
                {image && (
                    <img
                        src={
                            typeof image === "string"
                                ? image
                                : URL.createObjectURL(image)
                        }
                        className="step-image"
                        alt={step.title || "Image"}
                        style={{
                            maxHeight: "10rem",
                        }}
                    />
                )}
            </>
        );
    };

    const renderStepContent = (step) => {
        if (!step) return null;

        const stepType = normalizeType(step.type);

        switch (stepType) {
            case "button":
                return (
                    <button
                        className="btn-funnel btn-funnel-primary-funnel"
                        onClick={() => {
                            handleResponse(step.id, "clicked");
                            nextStep();
                        }}
                        style={{
                            ...(step.attributes?.button_color && {
                                backgroundColor: step.attributes.button_color,
                                background: step.attributes.button_color,
                                backgroundImage: "none",
                            }),
                            color:
                                step.attributes?.button_text_color || "#ffffff",
                        }}
                    >
                        {step.attributes?.button_text || "Continue"}
                    </button>
                );

            case "radio":
                return (
                    <div className="input-area-funnel">
                        <div className="options-container">
                            {step.attributes?.options?.map((option, index) => {
                                const selected =
                                    userResponses[step.id] === option;
                                return (
                                    <div
                                        key={index}
                                        className={`option-item ${
                                            selected ? "selected" : ""
                                        }`}
                                        onClick={() =>
                                            handleResponse(step.id, option)
                                        }
                                    >
                                        <input
                                            type="radio"
                                            checked={!!selected}
                                            onChange={() => {}}
                                        />
                                        <label>{option}</label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );

            case "input":
                return (
                    <div className="input-area-funnel">
                        <div className="form-group">
                            <input
                                type={
                                    step.attributes?.input_type === "number"
                                        ? "number"
                                        : step.attributes?.input_type ===
                                            "email"
                                          ? "email"
                                          : step.attributes?.input_type ===
                                              "date"
                                            ? "date"
                                            : step.attributes?.input_type ===
                                                "time"
                                              ? "time"
                                              : step.attributes?.input_type ===
                                                  "datetime"
                                                ? "datetime-local"
                                                : step.attributes
                                                        ?.input_type === "phone"
                                                  ? "tel"
                                                  : "text"
                                }
                                className="form-input"
                                placeholder={
                                    step.attributes?.placeholder ||
                                    "Enter your answer..."
                                }
                                onChange={(e) =>
                                    handleResponse(step.id, e.target.value)
                                }
                                onKeyPress={(e) => {
                                    if (
                                        e.key === "Enter" &&
                                        e.target.value.trim()
                                    ) {
                                        handleResponse(
                                            step.id,
                                            e.target.value.trim(),
                                        );
                                        nextStep();
                                    }
                                }}
                            />
                        </div>
                    </div>
                );

            case "multiselect":
                return (
                    <div className="input-area-funnel">
                        <div className="options-container">
                            {step.attributes?.options?.map((option, index) => {
                                const currentValues =
                                    userResponses[step.id] || [];
                                const isSelected =
                                    currentValues.includes(option);

                                return (
                                    <div
                                        key={index}
                                        className={`option-item ${
                                            isSelected ? "selected" : ""
                                        }`}
                                        onClick={() => {
                                            const newValues = isSelected
                                                ? currentValues.filter(
                                                      (v) => v !== option,
                                                  )
                                                : [...currentValues, option];
                                            handleResponse(step.id, newValues);
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {}}
                                        />
                                        <label>{option}</label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );

            case "slider":
                const sliderOptions = [
                    step.attributes?.min || "Min",
                    step.attributes?.mid || "Mid",
                    step.attributes?.max || "Max",
                ];
                const currentSliderValue =
                    userResponses[step.id] || step.attributes?.mid || "Mid";
                const sliderIndex = sliderOptions.indexOf(currentSliderValue);

                return (
                    <div className="input-area-funnel">
                        <div className="slider-container">
                            <div className="slider-labels">
                                <span>{step.attributes?.min || "Min"}</span>
                                <span>{step.attributes?.max || "Max"}</span>
                            </div>
                            <div className="slider-options">
                                {sliderOptions.map((option, index) => (
                                    <div
                                        key={index}
                                        className={`slider-option ${
                                            sliderIndex === index
                                                ? "active"
                                                : ""
                                        }`}
                                        onClick={() => {
                                            handleResponse(step.id, option);
                                        }}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case "range":
                const min = Number(step.attributes?.min ?? 0);
                const max = Number(step.attributes?.max ?? 100);
                const stepSize = Number(step.attributes?.step_size ?? 1);
                const unit = step.attributes?.unit || "";
                const currentRange =
                    userResponses[step.id] !== undefined
                        ? Number(userResponses[step.id])
                        : Math.floor((min + max) / 2);

                // Ensure value is within bounds
                const clampedValue = Math.max(min, Math.min(max, currentRange));
                const percentage = ((clampedValue - min) / (max - min)) * 100;

                return (
                    <div className="input-area-funnel">
                        <div className="slider-container">
                            <div className="slider-value">
                                {clampedValue}
                                {unit}
                            </div>
                            <div className="slider-track">
                                <div
                                    className="slider-fill"
                                    style={{ width: `${percentage}%` }}
                                />
                                <div
                                    className="slider-thumb"
                                    style={{ left: `${percentage}%` }}
                                    onMouseDown={(e) => {
                                        const track = e.target.parentNode;
                                        const rect =
                                            track.getBoundingClientRect();
                                        const isDragging = true;

                                        const handleMouseMove = (moveEvent) => {
                                            const x =
                                                moveEvent.clientX - rect.left;
                                            const percentage = Math.max(
                                                0,
                                                Math.min(
                                                    100,
                                                    (x / rect.width) * 100,
                                                ),
                                            );
                                            const newValue =
                                                min +
                                                (percentage / 100) *
                                                    (max - min);
                                            const steppedValue =
                                                Math.round(
                                                    newValue / stepSize,
                                                ) * stepSize;
                                            const finalValue = Math.max(
                                                min,
                                                Math.min(max, steppedValue),
                                            );

                                            handleResponse(step.id, finalValue);
                                        };

                                        const handleMouseUp = () => {
                                            document.removeEventListener(
                                                "mousemove",
                                                handleMouseMove,
                                            );
                                            document.removeEventListener(
                                                "mouseup",
                                                handleMouseUp,
                                            );
                                        };

                                        document.addEventListener(
                                            "mousemove",
                                            handleMouseMove,
                                        );
                                        document.addEventListener(
                                            "mouseup",
                                            handleMouseUp,
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                );

            case "rating":
                const maxStars = Number(step.attributes?.number_of_stars || 5);
                const selectedRating = Number(userResponses[step.id] || 0);
                return (
                    <div className="input-area-funnel">
                        <div className="rating-container">
                            {Array.from({ length: maxStars }).map((_, i) => {
                                const idx = i + 1;
                                const isSelected = idx <= selectedRating;
                                return (
                                    <span
                                        key={idx}
                                        className={`rating-star ${
                                            isSelected ? "selected" : ""
                                        }`}
                                        onClick={() =>
                                            handleResponse(step.id, idx)
                                        }
                                    >
                                        ★
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                );

            case "nps":
                const value = Number(userResponses[step.id]);
                return (
                    <div className="input-area-funnel">
                        <div className="nps-container">
                            <div className="nps-scale">
                                {Array.from({ length: 11 }).map((_, i) => (
                                    <button
                                        key={i}
                                        className={`nps-button ${
                                            value === i ? "selected" : ""
                                        }`}
                                        onClick={() =>
                                            handleResponse(step.id, i)
                                        }
                                    >
                                        {i}
                                    </button>
                                ))}
                            </div>
                            <div className="nps-labels">
                                <span>
                                    {step.attributes?.min_label || "Not likely"}
                                </span>
                                <span>
                                    {step.attributes?.max_label ||
                                        "Very likely"}
                                </span>
                            </div>
                        </div>
                    </div>
                );

            case "otp":
                const length = Number(
                    step.attributes?.otp_length || step.attributes?.length || 6,
                );
                const otpValue = String(userResponses[step.id] || "");
                return (
                    <div className="input-area-funnel">
                        <div className="otp-container">
                            {Array.from({ length }).map((_, idx) => (
                                <input
                                    key={idx}
                                    type="text"
                                    className="otp-input"
                                    maxLength={1}
                                    value={otpValue[idx] || ""}
                                    onChange={(e) => {
                                        const v = e.target.value
                                            .replace(/[^0-9A-Za-z]/g, "")
                                            .slice(0, 1);
                                        const arr = otpValue.split("");
                                        arr[idx] = v;
                                        const joined = arr.join("");
                                        handleResponse(step.id, joined);

                                        // Auto-focus next input
                                        if (v && idx < length - 1) {
                                            const nextInput =
                                                e.target.parentNode.children[
                                                    idx + 1
                                                ];
                                            if (nextInput) nextInput.focus();
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        // Handle backspace to go to previous input
                                        if (
                                            e.key === "Backspace" &&
                                            !e.target.value &&
                                            idx > 0
                                        ) {
                                            const prevInput =
                                                e.target.parentNode.children[
                                                    idx - 1
                                                ];
                                            if (prevInput) prevInput.focus();
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                );

            case "file":
                const fileType = step.attributes?.file_type;
                const accept = Array.isArray(fileType)
                    ? fileType
                          .map((ft) =>
                              ft === "image"
                                  ? "image/*"
                                  : ft === "video"
                                    ? "video/*"
                                    : ft === "audio"
                                      ? "audio/*"
                                      : ft === "document"
                                        ? ".pdf,.doc,.docx,.xls,.xlsx"
                                        : "*/*",
                          )
                          .join(",")
                    : fileType === "image"
                      ? "image/*"
                      : fileType === "video"
                        ? "video/*"
                        : fileType === "audio"
                          ? "audio/*"
                          : fileType === "document"
                            ? ".pdf,.doc,.docx,.xls,.xlsx"
                            : undefined;
                return (
                    <div className="input-area-funnel">
                        <div
                            className="file-upload-area"
                            onClick={() =>
                                document
                                    .getElementById(`file-${step.id}`)
                                    ?.click()
                            }
                        >
                            <div className="file-upload-icon">📎</div>
                            <div className="file-upload-text">
                                Click or drag to upload
                            </div>
                        </div>
                        <input
                            id={`file-${step.id}`}
                            type="file"
                            style={{ display: "none" }}
                            accept={accept}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleResponse(step.id, file.name);
                            }}
                        />
                        {userResponses[step.id] && (
                            <div className="file-selected">
                                Selected: {userResponses[step.id]}
                            </div>
                        )}
                    </div>
                );

            case "carousel":
                const rawImages = step.attributes?.images || [];
                const imgs = rawImages;
                const curr = carouselState[step.id] || 0;
                return (
                    <div className="input-area-funnel">
                        {!!imgs.length && (
                            <>
                                <div className="carousel-container">
                                    <div
                                        className="carousel-track"
                                        style={{
                                            transform: `translateX(-${
                                                curr * 100
                                            }%)`,
                                        }}
                                    >
                                        {imgs.map((src, i) => (
                                            <div
                                                key={i}
                                                className="carousel-slide"
                                            >
                                                <img
                                                    src={
                                                        typeof src === "string"
                                                            ? src
                                                            : URL.createObjectURL(
                                                                  src,
                                                              )
                                                    }
                                                    className="carousel-image"
                                                    alt={`slide-${i}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="carousel-controls">
                                    <button
                                        className="carousel-btn"
                                        disabled={curr === 0}
                                        onClick={() =>
                                            setCarouselState((s) => ({
                                                ...s,
                                                [step.id]: Math.max(
                                                    0,
                                                    (s[step.id] || 0) - 1,
                                                ),
                                            }))
                                        }
                                    >
                                        ‹
                                    </button>
                                    <div className="carousel-dots">
                                        {imgs.map((_, i) => (
                                            <div
                                                key={i}
                                                className={`carousel-dot ${
                                                    i === curr ? "active" : ""
                                                }`}
                                            />
                                        ))}
                                    </div>
                                    <button
                                        className="carousel-btn"
                                        disabled={curr >= imgs.length - 1}
                                        onClick={() =>
                                            setCarouselState((s) => ({
                                                ...s,
                                                [step.id]: Math.min(
                                                    imgs.length - 1,
                                                    (s[step.id] || 0) + 1,
                                                ),
                                            }))
                                        }
                                    >
                                        ›
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                );

            case "grid_selector":
            case "grid_selector_multiple":
                const items = step.attributes?.grid_items || [];
                const multi = stepType === "grid_selector_multiple";
                const selectedSet = new Set(
                    Array.isArray(userResponses[step.id])
                        ? userResponses[step.id]
                        : userResponses[step.id]
                          ? [userResponses[step.id]]
                          : [],
                );
                const onSelect = (val) => {
                    if (multi) {
                        const next = new Set(selectedSet);
                        if (next.has(val)) next.delete(val);
                        else next.add(val);
                        handleResponse(step.id, Array.from(next));
                    } else {
                        handleResponse(step.id, val);
                    }
                };
                return (
                    <div className="input-area-funnel">
                        <div className="image-grid-container">
                            {items.map((it, idx) => {
                                const imgSrc = it.image_url || "";
                                const image = it?.image; // file
                                const title = it.title || it.text || "";
                                const description = it.description || "";
                                const val = title || idx;
                                const selected = selectedSet.has(val);
                                return (
                                    <div
                                        key={idx}
                                        className={`image-grid-item ${
                                            selected ? "selected" : ""
                                        }`}
                                        onClick={() => onSelect(val)}
                                    >
                                        <div className="checkmark">✓</div>
                                        {imgSrc && (
                                            <img src={imgSrc} alt={title} />
                                        )}
                                        {image && (
                                            <img
                                                src={
                                                    typeof image === "string"
                                                        ? image
                                                        : URL.createObjectURL(
                                                              image,
                                                          )
                                                }
                                                alt={title}
                                            />
                                        )}
                                        {!!title && (
                                            <div className="image-grid-item-text">
                                                {title}
                                            </div>
                                        )}
                                        {!!description && (
                                            <div className="image-grid-item-description">
                                                {description}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );

            case "checkbox":
                const isChecked = userResponses[step.id] || false;

                return (
                    <div className="input-area-funnel">
                        <div
                            className="option-item"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                handleResponse(step.id, !isChecked);
                            }}
                        >
                            <input type="checkbox" checked={isChecked} />
                            <label>
                                {step.attributes?.label || "Check this option"}
                            </label>
                        </div>
                    </div>
                );

            case "processing":
                const tasks = step.attributes?.processing_steps || [];
                return (
                    <div className="input-area-funnel">
                        <div className="processing-screen">
                            {tasks.map((t, i) => (
                                <div key={i} className="processing-item">
                                    <div className="processing-label">
                                        <span>
                                            {t.step_title ||
                                                t.label ||
                                                `Step ${i + 1}`}
                                        </span>
                                        <span className="processing-percentage">
                                            {processingState.taskIndex > i
                                                ? "100%"
                                                : processingState.taskIndex ===
                                                    i
                                                  ? `${processingState.progress}%`
                                                  : "0%"}
                                        </span>
                                    </div>
                                    <div className="processing-bar-container">
                                        <div
                                            className={`processing-bar ${
                                                processingState.taskIndex === i
                                                    ? "active"
                                                    : ""
                                            }`}
                                            style={{
                                                width:
                                                    processingState.taskIndex >
                                                    i
                                                        ? "100%"
                                                        : processingState.taskIndex ===
                                                            i
                                                          ? `${processingState.progress}%`
                                                          : "0%",
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="input-area-funnel">
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <p style={{ color: "#6B7280" }}>
                                Unsupported step type: {step.type}
                            </p>
                        </div>
                    </div>
                );
        }
    };

    const isStepReady = () => {
        if (!currentStep) return false;
        if (currentStep.optional) return true;

        const response = userResponses[currentStep.id];
        if (!response) return false;

        if (Array.isArray(response)) {
            return response.length > 0;
        }

        return response !== "" && response !== null && response !== undefined;
    };

    if (steps.length === 0) {
        return (
            <div className="web-funnel-container" style={{ height: "100vh" }}>
                <div className="funnel-header">
                    <h1 className="funnel-title">Preview</h1>
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "400px",
                        color: "#6B7280",
                    }}
                >
                    No steps to preview. Add some steps to see the preview.
                </div>
            </div>
        );
    }

    return (
        <div
            className="web-funnel-container"
            style={{
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                width: "100%",
            }}
        >
            {/* Header */}
            <div
                className="funnel-header"
                style={{ marginBottom: "15px", paddingBottom: "15px" }}
            >
                <h1
                    className="funnel-title"
                    style={{ fontSize: "20px", marginBottom: "10px" }}
                >
                    Preview
                </h1>
            </div>

            {/* Steps Container */}
            <div
                id="webFunnelContainer"
                style={{
                    flex: 1,
                    overflow: "auto",
                    padding: "10px",
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    maxHeight: "calc(100vh - 200px)",
                }}
                className="funnel-scrollbar"
            >
                {isCompleted ? (
                    // Success/Completion Screen
                    <div
                        className="step-card completion-screen"
                        style={{
                            width: "100%",
                            margin: "0 auto",
                            height: "fit-content",
                            padding: "30px 20px",
                            maxWidth: "600px",
                        }}
                    >
                        <div
                            className="completion-icon"
                            style={{ fontSize: "50px" }}
                        >
                            ✅
                        </div>
                        <div
                            className="completion-title"
                            style={{ fontSize: "24px" }}
                        >
                            Success Title! 🎉
                        </div>
                        <div
                            className="completion-text"
                            style={{ fontSize: "16px" }}
                        >
                            Success Description!
                        </div>
                        <button
                            className="btn-funnel btn-funnel-primary-funnel"
                            onClick={() => {
                                setIsCompleted(false);
                                setCurrentStepIndex(0);
                                setUserResponses({});
                                setStepHistory([]);
                            }}
                            style={{ marginTop: "20px" }}
                        >
                            Start Over
                        </button>
                    </div>
                ) : (
                    currentStep && (
                        <div
                            key={currentStep?.id || currentStepIndex}
                            className="step-card"
                            style={{
                                width: "100%",
                                margin: "0 auto",
                                height: "fit-content",
                                padding: "24px 20px",
                                maxWidth: "600px",
                            }}
                        >
                            {/* Step Header */}
                            <div className="step-header">
                                <div className="step-number">
                                    {currentStepIndex + 1}
                                </div>
                                <div className="step-content">
                                    <div className="step-title">
                                        {currentStep.title}
                                        {currentStep.optional && (
                                            <span className="optional-badge">
                                                Optional
                                            </span>
                                        )}
                                    </div>
                                    {currentStep.description && (
                                        <div className="step-description">
                                            {currentStep.description}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Media */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: "100%",
                                }}
                            >
                                {renderMedia(currentStep)}
                            </Box>

                            {/* Step Content */}
                            {currentStep?.type !== "button" &&
                                renderStepContent(currentStep)}

                            {/* Navigation Buttons */}
                            <div className="button-group">
                                {stepHistory.length > 0 && (
                                    <button
                                        className="btn-funnel btn-funnel-secondary"
                                        onClick={previousStep}
                                    >
                                        Back
                                    </button>
                                )}

                                {currentStep.type !== "button" && (
                                    <button
                                        className="btn-funnel btn-funnel-primary-funnel"
                                        onClick={nextStep}
                                        disabled={!isStepReady()}
                                        style={{
                                            ...(currentStep.attributes
                                                ?.button_color && {
                                                backgroundColor:
                                                    currentStep.attributes
                                                        .button_color,
                                                background:
                                                    currentStep.attributes
                                                        .button_color,
                                                backgroundImage: "none",
                                            }),
                                            color:
                                                currentStep.attributes
                                                    ?.button_text_color ||
                                                "#ffffff",
                                        }}
                                    >
                                        {currentStepIndex === steps.length - 1
                                            ? "Submit"
                                            : "Continue"}
                                    </button>
                                )}
                                {currentStep.type === "button" && (
                                    <button
                                        className="btn-funnel btn-funnel-primary-funnel"
                                        onClick={() => {
                                            handleResponse(
                                                currentStep.id,
                                                "clicked",
                                            );
                                            nextStep();
                                        }}
                                        style={{
                                            ...(currentStep.attributes
                                                ?.button_color && {
                                                backgroundColor:
                                                    currentStep.attributes
                                                        .button_color,
                                                background:
                                                    currentStep.attributes
                                                        .button_color,
                                                backgroundImage: "none",
                                            }),
                                            color:
                                                currentStep.attributes
                                                    ?.button_text_color ||
                                                "#ffffff",
                                        }}
                                    >
                                        {currentStep.attributes?.button_text ||
                                            "Continue"}
                                    </button>
                                )}

                                {currentStep.optional && (
                                    <button
                                        className="btn-funnel btn-funnel-secondary"
                                        onClick={skipStep}
                                    >
                                        Skip
                                    </button>
                                )}
                            </div>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default PreviewContainer;
